import React, { useEffect, useRef, useState, useCallback } from 'react';

const GOOGLE_KEY = process.env.REACT_APP_GOOGLE_PLACES_KEY;

let scriptLoaded = false;
let scriptLoading = false;
const loadCallbacks = [];

const loadGoogleScript = () => {
  return new Promise((resolve) => {
    if (window.google?.maps?.places) { resolve(); return; }
    if (scriptLoaded) { resolve(); return; }
    loadCallbacks.push(resolve);
    if (scriptLoading) return;
    scriptLoading = true;
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_KEY}&libraries=places&v=weekly`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      scriptLoaded = true;
      loadCallbacks.forEach(cb => cb());
      loadCallbacks.length = 0;
    };
    script.onerror = () => {
      loadCallbacks.forEach(cb => cb());
      loadCallbacks.length = 0;
    };
    document.head.appendChild(script);
  });
};

export const AddressAutocomplete = ({ value, onChange, onPlaceSelect, placeholder = 'Start typing an address...', className = '', required = false, testId = 'address-autocomplete' }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [ready, setReady] = useState(false);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);
  const sessionTokenRef = useRef(null);

  useEffect(() => {
    if (!GOOGLE_KEY) return;
    loadGoogleScript().then(() => {
      setReady(true);
      try {
        sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
      } catch {}
    });
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchSuggestions = useCallback(async (input) => {
    if (!ready || !input || input.length < 3) { setSuggestions([]); return; }
    
    try {
      const service = new window.google.maps.places.AutocompleteService();
      service.getPlacePredictions(
        {
          input,
          componentRestrictions: { country: ['ca', 'us', 'mx'] },
          types: ['address'],
          sessionToken: sessionTokenRef.current,
        },
        (predictions, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
            setSuggestions(predictions.map(p => ({
              placeId: p.place_id,
              description: p.description,
              mainText: p.structured_formatting?.main_text || '',
              secondaryText: p.structured_formatting?.secondary_text || '',
            })));
            setShowDropdown(true);
          } else {
            setSuggestions([]);
          }
        }
      );
    } catch {
      setSuggestions([]);
    }
  }, [ready]);

  const handleInput = (e) => {
    const val = e.target.value;
    onChange(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(val), 300);
  };

  const handleSelect = async (suggestion) => {
    setShowDropdown(false);
    onChange(suggestion.description);

    try {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ placeId: suggestion.placeId }, (results, status) => {
        if (status === 'OK' && results?.[0]) {
          const result = results[0];
          const lat = result.geometry?.location?.lat();
          const lng = result.geometry?.location?.lng();
          
          let city = '', country = '', province = '', postalCode = '';
          (result.address_components || []).forEach(c => {
            if (c.types.includes('locality')) city = c.long_name;
            if (c.types.includes('administrative_area_level_1')) province = c.short_name;
            if (c.types.includes('country')) country = c.long_name;
            if (c.types.includes('postal_code')) postalCode = c.long_name;
          });

          if (onPlaceSelect) {
            onPlaceSelect({
              address: suggestion.description,
              place_id: suggestion.placeId,
              lat, lng, city, country, province, postalCode,
            });
          }
        }
      });
      // Refresh session token after selection
      sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
    } catch {}
  };

  return (
    <div ref={containerRef} className="relative">
      <input
        type="text"
        value={value}
        onChange={handleInput}
        onFocus={() => { if (suggestions.length) setShowDropdown(true); }}
        placeholder={placeholder}
        className={className}
        required={required}
        data-testid={testId}
        autoComplete="off"
      />
      {showDropdown && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border-2 border-gray-200 rounded-lg shadow-xl max-h-60 overflow-y-auto" data-testid="address-suggestions">
          {suggestions.map((s, i) => (
            <button
              key={s.placeId}
              type="button"
              onClick={() => handleSelect(s)}
              className="w-full text-left px-4 py-3 hover:bg-orange-50 border-b border-gray-50 last:border-0 transition-colors"
              data-testid={`address-suggestion-${i}`}
            >
              <p className="text-sm font-semibold text-gray-900">{s.mainText}</p>
              <p className="text-xs text-gray-500">{s.secondaryText}</p>
            </button>
          ))}
          <div className="px-4 py-1.5 bg-gray-50 flex items-center justify-end">
            <img src="https://developers.google.com/static/maps/documentation/images/powered_by_google_on_white.png" alt="Powered by Google" className="h-4" />
          </div>
        </div>
      )}
    </div>
  );
};


export const VenueAutocomplete = ({ value, onChange, onPlaceSelect, placeholder = 'Search for your venue...', className = '', required = false, testId = 'venue-autocomplete' }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [ready, setReady] = useState(false);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);
  const sessionTokenRef = useRef(null);

  useEffect(() => {
    if (!GOOGLE_KEY) return;
    loadGoogleScript().then(() => {
      setReady(true);
      try { sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken(); } catch {}
    });
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchSuggestions = useCallback(async (input) => {
    if (!ready || !input || input.length < 2) { setSuggestions([]); return; }
    try {
      const service = new window.google.maps.places.AutocompleteService();
      service.getPlacePredictions(
        { input, componentRestrictions: { country: ['ca', 'us', 'mx'] }, types: ['establishment'], sessionToken: sessionTokenRef.current },
        (predictions, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
            setSuggestions(predictions.map(p => ({
              placeId: p.place_id,
              description: p.description,
              mainText: p.structured_formatting?.main_text || '',
              secondaryText: p.structured_formatting?.secondary_text || '',
            })));
            setShowDropdown(true);
          } else { setSuggestions([]); }
        }
      );
    } catch { setSuggestions([]); }
  }, [ready]);

  const handleInput = (e) => {
    const val = e.target.value;
    onChange(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(val), 300);
  };

  const handleSelect = async (suggestion) => {
    setShowDropdown(false);
    onChange(suggestion.mainText);
    try {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ placeId: suggestion.placeId }, (results, status) => {
        if (status === 'OK' && results?.[0]) {
          const result = results[0];
          const lat = result.geometry?.location?.lat();
          const lng = result.geometry?.location?.lng();
          let city = '', country = '', province = '';
          (result.address_components || []).forEach(c => {
            if (c.types.includes('locality')) city = c.long_name;
            if (c.types.includes('administrative_area_level_1')) province = c.short_name;
            if (c.types.includes('country')) country = c.long_name;
          });
          if (onPlaceSelect) {
            onPlaceSelect({ name: suggestion.mainText, address: result.formatted_address, place_id: suggestion.placeId, lat, lng, city, country, province });
          }
        }
      });
      sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
    } catch {}
  };

  return (
    <div ref={containerRef} className="relative">
      <input type="text" value={value} onChange={handleInput} onFocus={() => { if (suggestions.length) setShowDropdown(true); }}
        placeholder={placeholder} className={className} required={required} data-testid={testId} autoComplete="off" />
      {showDropdown && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border-2 border-gray-200 rounded-lg shadow-xl max-h-60 overflow-y-auto" data-testid="venue-suggestions">
          {suggestions.map((s, i) => (
            <button key={s.placeId} type="button" onClick={() => handleSelect(s)}
              className="w-full text-left px-4 py-3 hover:bg-orange-50 border-b border-gray-50 last:border-0 transition-colors" data-testid={`venue-suggestion-${i}`}>
              <p className="text-sm font-semibold text-gray-900">{s.mainText}</p>
              <p className="text-xs text-gray-500">{s.secondaryText}</p>
            </button>
          ))}
          <div className="px-4 py-1.5 bg-gray-50 flex items-center justify-end">
            <img src="https://developers.google.com/static/maps/documentation/images/powered_by_google_on_white.png" alt="Powered by Google" className="h-4" />
          </div>
        </div>
      )}
    </div>
  );
};

export const getStaticMapUrl = (lat, lng, zoom = 15, width = 400, height = 200) => {
  if (!GOOGLE_KEY || !lat || !lng) return null;
  return `https://maps.googleapis.com/maps/api/staticmap?center=${lat},${lng}&zoom=${zoom}&size=${width}x${height}&scale=2&markers=color:red%7C${lat},${lng}&key=${GOOGLE_KEY}`;
};
